
import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu } from 'lucide-react';
import TudoConversorLogo from './TudoConversorLogo.jsx';
import { getTranslatedCategories } from '@/utils/conversionDataI18n.js';
import { useI18n } from '@/i18n/index.jsx';
import { trackEvent, Events } from '@/utils/analytics.js';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";

const NAV = {
  pt: {
    home: 'Início',
    about: 'Sobre',
    contact: 'Contato',
    imc: 'Calculadora IMC',
    categories: 'Categorias',
    converters: 'Conversores',
    menu: 'Menu de Navegação',
    catLabel: 'Categorias de Conversores'
  },
  en: {
    home: 'Home',
    about: 'About',
    contact: 'Contact',
    imc: 'BMI Calculator',
    categories: 'Categories',
    converters: 'Converters',
    menu: 'Navigation Menu',
    catLabel: 'Converter Categories'
  }
};

function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { dict } = useI18n();
  
  const lang = location.pathname.startsWith('/en') ? 'en' : 'pt';
  const isEN = lang === 'en';
  
  const categories = getTranslatedCategories(dict, lang);
  const T = NAV[lang] || NAV.pt;

  const handleLangChange = (newLang) => {
    const currentPath = location.pathname;
    trackEvent(Events.LANGUAGE_SWITCH, { to: newLang });
    
    if (newLang === 'pt') {
      let ptPath = currentPath.replace(/^\/en(\/|$)/, '/') || '/';
      if (!ptPath.endsWith('/')) ptPath += '/';
      navigate(ptPath);
    } else {
      let enPath = currentPath.startsWith('/en') ? currentPath : `/en${currentPath === '/' ? '' : currentPath}`;
      if (!enPath.endsWith('/')) enPath += '/';
      navigate(enPath);
    }
  };

  const getLocalizedPath = (path) => {
    const normalized = path.endsWith('/') ? path : `${path}/`;
    return isEN ? `/en${normalized === '/' ? '' : normalized}` : normalized;
  };

  return (
    <header className="bg-primary text-primary-foreground border-b border-primary/20 shadow-sm sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link 
            to={getLocalizedPath('/')} 
            className="hover:opacity-90 transition-opacity flex-shrink-0"
            aria-label={isEN ? 'Back to home' : 'Voltar ao início'}
          >
            <TudoConversorLogo />
          </Link>
          
          <div className="flex items-center gap-4">
            <nav className="hidden lg:flex items-center gap-2" aria-label={isEN ? 'Main navigation' : 'Navegação principal'}>
              <NavigationMenu>
                <NavigationMenuList>
                  
                  <NavigationMenuItem>
                    <NavigationMenuLink asChild className="group inline-flex h-10 w-max items-center justify-center rounded-md bg-transparent px-4 py-2 text-sm font-medium text-white/90 hover:text-white hover:bg-white/10 transition-colors">
                      <Link to={getLocalizedPath('/')}>
                        {T.home}
                      </Link>
                    </NavigationMenuLink>
                  </NavigationMenuItem>

                  <NavigationMenuItem>
                    <NavigationMenuLink asChild className="group inline-flex h-10 w-max items-center justify-center rounded-md bg-transparent px-4 py-2 text-sm font-medium text-white/90 hover:text-white hover:bg-white/10 transition-colors">
                      <Link to={getLocalizedPath('/sobre')}>
                        {T.about}
                      </Link>
                    </NavigationMenuLink>
                  </NavigationMenuItem>

                  <NavigationMenuItem>
                    <NavigationMenuLink asChild className="group inline-flex h-10 w-max items-center justify-center rounded-md bg-transparent px-4 py-2 text-sm font-medium text-white/90 hover:text-white hover:bg-white/10 transition-colors">
                      <Link to={getLocalizedPath('/contato')}>
                        {T.contact}
                      </Link>
                    </NavigationMenuLink>
                  </NavigationMenuItem>
                  
                  <NavigationMenuItem>
                    <NavigationMenuLink asChild className="group inline-flex h-10 w-max items-center justify-center rounded-md bg-transparent px-4 py-2 text-sm font-medium text-white/90 hover:text-white hover:bg-white/10 transition-colors">
                      <Link to={getLocalizedPath('/calculadora/imc')}>
                        {T.imc}
                      </Link>
                    </NavigationMenuLink>
                  </NavigationMenuItem>
                  
                  <NavigationMenuItem className="relative">
                    <NavigationMenuTrigger className="bg-transparent text-white/90 hover:text-white hover:bg-white/10 focus:bg-white/10 data-[state=open]:bg-white/10">
                      {T.categories}
                    </NavigationMenuTrigger>
                    <NavigationMenuContent className="right-0 left-auto origin-top-right">
                      <ul className="grid w-[90vw] max-w-[400px] gap-3 p-4 md:w-[600px] md:grid-cols-2 lg:w-[800px] lg:grid-cols-3 bg-popover text-popover-foreground">
                        {categories.map((category) => (
                          <li key={category.id}>
                            <Link
                              to={getLocalizedPath(`/categoria/${category.id}`)}
                              className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                            >
                              <div className="text-sm font-medium leading-none">{category.name}</div>
                              <p className="line-clamp-2 text-xs leading-snug text-muted-foreground mt-1">
                                {category.converters.length} {T.converters}
                              </p>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </NavigationMenuContent>
                  </NavigationMenuItem>
                </NavigationMenuList>
              </NavigationMenu>
            </nav>

            <div className="hidden sm:flex bg-black/20 p-1 rounded-lg">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleLangChange('pt')}
                className={`px-3 py-1.5 text-xs font-bold transition-colors rounded-md ${!isEN ? 'bg-white text-primary hover:bg-white hover:text-primary' : 'text-white/70 hover:text-white hover:bg-white/10'}`}
              >
                PT
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleLangChange('en')}
                className={`px-3 py-1.5 text-xs font-bold transition-colors rounded-md ${isEN ? 'bg-white text-primary hover:bg-white hover:text-primary' : 'text-white/70 hover:text-white hover:bg-white/10'}`}
              >
                EN
              </Button>
            </div>

            <div className="lg:hidden flex items-center gap-2">
              <div className="flex sm:hidden bg-black/20 p-1 rounded-lg">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleLangChange('pt')}
                  className={`px-3 py-1.5 text-xs font-bold transition-colors rounded-md ${!isEN ? 'bg-white text-primary hover:bg-white hover:text-primary' : 'text-white/70 hover:text-white hover:bg-white/10'}`}
                >
                  PT
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleLangChange('en')}
                  className={`px-3 py-1.5 text-xs font-bold transition-colors rounded-md ${isEN ? 'bg-white text-primary hover:bg-white hover:text-primary' : 'text-white/70 hover:text-white hover:bg-white/10'}`}
                >
                  EN
                </Button>
              </div>

              <Sheet open={isOpen} onOpenChange={setIsOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="text-white hover:bg-white/10" aria-label={isEN ? 'Open menu' : 'Abrir menu'}>
                    <Menu className="h-6 w-6" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-[300px] sm:w-[400px] bg-background p-0 border-l border-border">
                  <SheetTitle className="sr-only">{isEN ? 'Navigation Menu' : 'Menu de Navegação'}</SheetTitle>
                  <div className="p-6 border-b border-border bg-primary">
                    <TudoConversorLogo />
                  </div>
                  <ScrollArea className="h-[calc(100vh-5rem)] pb-10">
                    <nav className="flex flex-col p-6 gap-6" aria-label={isEN ? 'Mobile navigation' : 'Navegação móvel'}>
                      <Link 
                        to={getLocalizedPath('/')} 
                        onClick={() => setIsOpen(false)}
                        className="text-lg font-medium text-foreground hover:text-primary transition-colors"
                      >
                        {T.home}
                      </Link>
                      <Link 
                        to={getLocalizedPath('/sobre')} 
                        onClick={() => setIsOpen(false)}
                        className="text-lg font-medium text-foreground hover:text-primary transition-colors"
                      >
                        {T.about}
                      </Link>
                      <Link 
                        to={getLocalizedPath('/contato')} 
                        onClick={() => setIsOpen(false)}
                        className="text-lg font-medium text-foreground hover:text-primary transition-colors"
                      >
                        {T.contact}
                      </Link>
                      <Link 
                        to={getLocalizedPath('/calculadora/imc')} 
                        onClick={() => setIsOpen(false)}
                        className="text-lg font-medium text-foreground hover:text-primary transition-colors"
                      >
                        {T.imc}
                      </Link>
                      
                      <div className="space-y-4 pt-4 border-t border-border">
                        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-0 mt-0">
                          {T.catLabel}
                        </h2>
                        <div className="flex flex-col gap-3">
                          {categories.map((category) => (
                            <Link
                              key={category.id}
                              to={getLocalizedPath(`/categoria/${category.id}`)}
                              onClick={() => setIsOpen(false)}
                              className="text-base text-foreground hover:text-primary transition-colors py-1"
                            >
                              {category.name}
                            </Link>
                          ))}
                        </div>
                      </div>
                    </nav>
                  </ScrollArea>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}

export default Header;
